import { defineStore } from 'pinia'
import type { IBCLabSettings } from '@team-milva/lab-models/invoicing'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'
import { cloneDeep, omitBy } from 'lodash-es'
import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import { db } from '@/firebase/init'

const DEFAULT_BC_SETTINGS: IBCLabSettings = {
  type: 'business-central',
  options: {
    isEnabled: false,
    isConnected: false,
    paymentTermsIncludesCurrentMonth: false,
    isAutomaticInvoicingEnabled: true,
    isInvoiceCompleteWhenPosted: false,
  },
  tenantID: '',
  environmentName: '',
  companyID: '',
  companyName: '',
  paymentTermsDays: 30,
}

// bc-settings is fully typed (see managementApi('get'/'patch', 'lab-accounts/{labAccountID}/bc-settings', ...) below).
// The DEV lookup/bc-dev endpoints below still use the untyped cast because their query-string
// parameters (lookupType, environmentName, companyID, ...) aren't part of the typed request
// schema — the OpenAPI spec only declares path params for these routes.
type TUntypedApiClient = (
  method: string,
  path: string,
  opts?: Record<string, unknown>
) => Promise<{ success: boolean; data: Record<string, unknown> | null }>

const untypedManagementApi = managementApi as unknown as TUntypedApiClient

type TBcItemMappingsForm = {
  analyses: Record<string, string>
  rushFees: Record<string, string>
  addons: Record<string, string>
}

export type TBcSettingsForm = {
  isEnabled: boolean
  isConnected: boolean
  tenantID: string
  environmentName: string
  companyID: string
  companyName: string
  isAutomaticInvoicingEnabled: boolean
  isInvoiceCompleteWhenPosted: boolean
  paymentTermsDays: string
  paymentTermsIncludesCurrentMonth: boolean
  itemMappings: TBcItemMappingsForm
}

type TBcOption = { value: string; text: string }

const EMPTY_FORM: TBcSettingsForm = {
  isEnabled: false,
  isConnected: false,
  tenantID: '',
  environmentName: '',
  companyID: '',
  companyName: '',
  isAutomaticInvoicingEnabled: true,
  isInvoiceCompleteWhenPosted: false,
  paymentTermsDays: '30',
  paymentTermsIncludesCurrentMonth: false,
  itemMappings: {
    analyses: {},
    rushFees: {},
    addons: {},
  },
}

function settingsToForm(settings: IBCLabSettings): TBcSettingsForm {
  return {
    isEnabled: settings.options.isEnabled,
    isConnected: settings.options.isConnected,
    tenantID: settings.tenantID,
    environmentName: settings.environmentName,
    companyID: settings.companyID,
    companyName: settings.companyName ?? '',
    isAutomaticInvoicingEnabled: settings.options.isAutomaticInvoicingEnabled ?? true,
    isInvoiceCompleteWhenPosted: settings.options.isInvoiceCompleteWhenPosted ?? false,
    paymentTermsDays: (settings.paymentTermsDays ?? 30).toString(),
    paymentTermsIncludesCurrentMonth:
      settings.options.paymentTermsIncludesCurrentMonth ?? false,
    itemMappings: {
      analyses: { ...(settings.itemMappings?.analyses ?? {}) },
      rushFees: { ...(settings.itemMappings?.rushFees ?? {}) },
      addons: { ...(settings.itemMappings?.addons ?? {}) },
    },
  }
}

export const useBcSettingsStore = defineStore('bcSettings', {
  state: () => ({
    form: cloneDeep(EMPTY_FORM),
    isLoaded: false,
    isLoading: false,
    isSavingConnection: false,
    isSavingDefaultValues: false,
    isSavingItemMappings: false,
    loadError: null as string | null,

    // Populates the Company ID combobox while filling in the connection form — keyed off
    // whatever Tenant ID/Environment Name is currently typed, not saved settings. See
    // loadBcCompanies below.
    bcCompanies: [] as TBcOption[],
    bcCompaniesLookupIssue: null as string | null,
    isLoadingBcCompanies: false,

    // Live view of just the scheduling/automation-relevant fields, kept in sync via `onSnapshot`
    // — unlike `form`, this is never mutated by the edit page, so it's safe to overwrite on every
    // remote change. Read-only consumers (e.g. the "automatic invoicing" column, the per-order
    // toggle's visibility) use this instead of `form` so they reflect BC settings edits made
    // elsewhere without needing a page reload.
    liveInvoicingSettings: null as {
      isAutomaticInvoicingEnabled: boolean
      isInvoiceCompleteWhenPosted: boolean
      /** The connection the lab is on now — what an invoice's own connection is compared with. */
      tenantID: string
      environmentName: string
    } | null,
    unsubscribeLiveInvoicingSettings: null as (() => void) | null,
  }),

  actions: {
    // Idempotent — safe even if the global router guard already loaded this for the user (see
    // router/index.ts's beforeEach checklist, gated on the lab account's cheap isEnabled flag).
    // Reads Firestore directly rather than via managementApi — the settings this form needs are
    // safe for the client to read directly (there is no per-lab secret any more — the app-only
    // connection authenticates as Milva's own app registration), and skipping the Cloud Function
    // round trip removes 2-3 sequential server-side Firestore reads from the settings page's load
    // time. This is a one-time load into an edit buffer, not a live subscription — `form` is
    // mutated directly by the edit forms below, so re-syncing it on every remote change would
    // clobber in-progress edits.
    async loadSettings(): Promise<void> {
      if (this.isLoaded) return

      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id
      if (!labAccountID) return

      this.isLoading = true
      this.loadError = null

      try {
        const invoicing = accountStore.labAccount?.integration?.invoicing
        const integrationID =
          invoicing?.type === 'business-central'
            ? invoicing.businessCentralIntegrationID
            : null
        const options = {
          isEnabled:
            invoicing?.type === 'business-central'
              ? invoicing.options.isEnabled
              : false,
          isConnected:
            invoicing?.type === 'business-central'
              ? invoicing.options.isConnected
              : false,
        }

        let settings: IBCLabSettings = { ...DEFAULT_BC_SETTINGS, options }

        if (integrationID) {
          const integrationSnap = await getDoc(
            doc(db, 'Apps', '-lab-management-', 'Integrations', integrationID)
          )
          if (integrationSnap.exists()) {
            // The physical Integrations doc stores paymentTermsIncludesCurrentMonth,
            // isAutomaticInvoicingEnabled and isInvoiceCompleteWhenPosted flat (see lab-cloud's IBcIntegrationDoc) — only
            // isEnabled/isConnected live on the lab account. Pull the flat fields out before
            // spreading so they land nested under `options`, matching IBCLabSettings's shape.
            // A flat field left out here reads as its default in `settingsToForm`: the automatic
            // invoicing switch showed on for a lab that had it off.
            const {
              paymentTermsIncludesCurrentMonth,
              isAutomaticInvoicingEnabled,
              isInvoiceCompleteWhenPosted,
              ...integrationData
            } = integrationSnap.data() as Omit<IBCLabSettings, 'options'> &
              Pick<
                IBCLabSettings['options'],
                | 'paymentTermsIncludesCurrentMonth'
                | 'isAutomaticInvoicingEnabled'
                | 'isInvoiceCompleteWhenPosted'
              >
            settings = {
              ...integrationData,
              type: 'business-central',
              options: {
                ...options,
                paymentTermsIncludesCurrentMonth,
                isAutomaticInvoicingEnabled,
                isInvoiceCompleteWhenPosted,
              },
            }
          }
          this.subscribeLiveInvoicingSettings(integrationID)
        }

        this.form = settingsToForm(settings)
        this.isLoaded = true
      } catch {
        this.loadError = 'load-failed'
      } finally {
        this.isLoading = false
      }
    },

    subscribeLiveInvoicingSettings(integrationID: string): void {
      if (this.unsubscribeLiveInvoicingSettings) return
      this.unsubscribeLiveInvoicingSettings = onSnapshot(
        doc(db, 'Apps', '-lab-management-', 'Integrations', integrationID),
        (snap) => {
          // This doc stores these fields flat (see IBcIntegrationDoc in lab-cloud) — it is
          // not a full IBCLabSettings, so don't cast to it here.
          const data = snap.data() as
            | {
                isAutomaticInvoicingEnabled?: boolean
                isInvoiceCompleteWhenPosted?: boolean
                tenantID?: string
                environmentName?: string
              }
            | undefined
          this.liveInvoicingSettings = {
            isAutomaticInvoicingEnabled: data?.isAutomaticInvoicingEnabled ?? true,
            isInvoiceCompleteWhenPosted: data?.isInvoiceCompleteWhenPosted ?? false,
            tenantID: data?.tenantID ?? '',
            environmentName: data?.environmentName ?? '',
          }
        }
      )
    },

    async saveConnectionSettings(): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id
      if (!labAccountID) throw new Error('No lab account')

      // Never save on top of a failed load — the form would be defaults, not the lab's config.
      if (this.loadError) throw new Error('settings-not-loaded')

      this.isSavingConnection = true
      try {
        const result = await managementApi(
          'patch',
          'lab-accounts/{labAccountID}/bc-settings',
          {
            path: { labAccountID },
            body: {
              isEnabled: this.form.isEnabled,
              tenantID: this.form.tenantID,
              environmentName: this.form.environmentName,
              companyID: this.form.companyID,
              // Captured from the company picker's own option text — the BC web client's
              // deep-link `company` parameter takes the display name, not the ID. Sent as ''
              // when the ID was typed by hand rather than picked, which leaves it unset.
              ...(this.form.companyName ? { companyName: this.form.companyName } : {}),
              isAutomaticInvoicingEnabled: this.form.isAutomaticInvoicingEnabled,
              isInvoiceCompleteWhenPosted: this.form.isInvoiceCompleteWhenPosted,
            },
          }
        )
        if (!result.success) throw new Error(result.message ?? 'save-failed')
      } finally {
        this.isSavingConnection = false
      }
    },

    async saveDefaultValues(): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id
      if (!labAccountID) throw new Error('No lab account')

      if (this.loadError) throw new Error('settings-not-loaded')

      this.isSavingDefaultValues = true
      try {
        const result = await managementApi(
          'patch',
          'lab-accounts/{labAccountID}/bc-settings',
          {
            path: { labAccountID },
            body: {
              paymentTermsDays:
                this.form.paymentTermsDays !== ''
                  ? Number(this.form.paymentTermsDays)
                  : 30,
              paymentTermsIncludesCurrentMonth:
                this.form.paymentTermsIncludesCurrentMonth,
              // TODO: remove cast after deploying and re-running openapi:gen — codegen still has
              // the old `invoiceEmailDelivery` field, since dev hasn't been deployed with this change yet.
            } as unknown as {
              paymentTermsDays: number
              paymentTermsIncludesCurrentMonth: boolean
            },
          }
        )
        if (!result.success) throw new Error(result.message ?? 'save-failed')
      } finally {
        this.isSavingDefaultValues = false
      }
    },

    // Saves one language's value independently of the settings page's own default-values save —
    // used by the Lab Translations page, where each language column is edited/saved separately.

    async saveItemMappings(): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id
      if (!labAccountID) throw new Error('No lab account')

      if (this.loadError) throw new Error('settings-not-loaded')

      this.isSavingItemMappings = true
      try {
        const result = await managementApi(
          'patch',
          'lab-accounts/{labAccountID}/bc-settings',
          {
            path: { labAccountID },
            body: {
              itemMappings: {
                analyses: omitBy(
                  this.form.itemMappings.analyses,
                  (value) => !value
                ),
                rushFees: omitBy(
                  this.form.itemMappings.rushFees,
                  (value) => !value
                ),
                addons: omitBy(this.form.itemMappings.addons, (value) => !value),
              },
            },
          }
        )
        if (!result.success) throw new Error(result.message ?? 'save-failed')
      } finally {
        this.isSavingItemMappings = false
      }
    },

    async createTestBcItems(
      items: { id: string; name: string }[]
    ): Promise<{
      created: {
        id: string
        itemNumber: string
        appliedTaxGroup: boolean
        appliedGeneralProductPostingGroup: boolean
        taxGroupRejectionReason?: string
        generalProductPostingGroupRejectionReason?: string
      }[]
      failed: { id: string; message: string }[]
    }> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id
      if (!labAccountID) throw new Error('No lab account')

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/bc-dev/create-test-items',
        {
          path: { labAccountID },
          body: { items },
        }
      )
      if (!result.success) throw new Error('create-test-items-failed')

      return result.data
    },

    async createTestBcCustomer(
      target:
        | { customerAccountID: string }
        | { privateCustomerID: string }
        | { insuranceCustomerAccountID: string }
    ): Promise<{ customerNumber: string; displayName: string }> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id
      if (!labAccountID) throw new Error('No lab account')

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/bc-dev/create-test-customer',
        { path: { labAccountID }, body: target }
      )
      if (!result.success) throw new Error(result.message ?? 'create-test-customer-failed')

      return result.data
    },

    async checkBcItemMappings(current: {
      itemMappings: {
        analyses: Record<string, string>
        rushFees: Record<string, string>
        addons: Record<string, string>
      }
      customerNumbers: Record<string, string>
    }): Promise<
      {
        id: string
        itemNumber?: string
        customerNumber?: string
        status: 'unmapped' | 'valid' | 'invalid' | 'incomplete' | 'check-failed'
        bcDisplayName?: string
        missingFields?: string[]
        checkError?: string
      }[]
    > {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id
      if (!labAccountID) throw new Error('No lab account')

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/bc-settings/check-item-mappings',
        {
          path: { labAccountID },
          body: {
            itemMappings: current.itemMappings,
            customerNumbers: current.customerNumbers,
          },
        }
      )
      if (!result.success) throw new Error('check-item-mappings-failed')

      return result.data.results
    },

    ensureItemMappingKeys(analysisIDs: string[], tatIDs: string[], addonIDs: string[]): void {
      for (const id of analysisIDs) {
        if (!(id in this.form.itemMappings.analyses)) {
          this.form.itemMappings.analyses[id] = ''
        }
      }
      for (const id of tatIDs) {
        if (!(id in this.form.itemMappings.rushFees)) {
          this.form.itemMappings.rushFees[id] = ''
        }
      }
      for (const id of addonIDs) {
        if (!(id in this.form.itemMappings.addons)) {
          this.form.itemMappings.addons[id] = ''
        }
      }
    },

    // Populates the Company ID combobox from a Tenant ID/Environment Name pair that may not be
    // saved yet — mirrors the "Verificer forbindelse" override pattern. Still requires the
    // Client ID to already be registered in BC for that environment; a failure here (e.g.
    // `bc-permission-set-missing`) is surfaced as-is rather than retried, since the lab still
    // needs to go do that registration regardless.
    async loadBcCompanies(
      tenantID: string,
      environmentName: string
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id
      if (!labAccountID || !tenantID || !environmentName) return

      this.isLoadingBcCompanies = true
      this.bcCompaniesLookupIssue = null
      try {
        const result = await untypedManagementApi(
          'get',
          '/lab-accounts/{labAccountID}/bc-auth/companies',
          {
            path: { labAccountID },
            query: { tenantID, environmentName },
          }
        )
        if (result.success && result.data) {
          const data = result.data as { items: TBcOption[] }
          this.bcCompanies = data.items
        } else {
          this.bcCompaniesLookupIssue =
            (result as { message?: string }).message ?? 'lookup-failed'
        }
      } catch {
        this.bcCompaniesLookupIssue = 'lookup-failed'
      } finally {
        this.isLoadingBcCompanies = false
      }
    },

    resetStore(): void {
      this.unsubscribeLiveInvoicingSettings?.()
      this.unsubscribeLiveInvoicingSettings = null
      this.liveInvoicingSettings = null
      this.form = cloneDeep(EMPTY_FORM)
      this.isLoaded = false
      this.isLoading = false
      this.isSavingConnection = false
      this.isSavingDefaultValues = false
      this.isSavingItemMappings = false
      this.loadError = null
      this.bcCompanies = []
      this.bcCompaniesLookupIssue = null
      this.isLoadingBcCompanies = false
    },
  },
})

